import { JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSearch } from 'react-icons/fa';
import { FiMail } from 'react-icons/fi';
import { Button } from '../../components/button';
import { Error } from '../../components/error';
import { Form } from '../../components/form';
import { User } from '../../dto/user';
import { useFirebase } from '../../hooks/firebase.hook';
import { useLayout } from '../../hooks/layout.hook';
import { useNavigation } from '../../hooks/navigation.hook';
import { usePartnership } from '../../hooks/partnership.hook';

export interface FormData {
  mail: string;
}

export default function PartnerAddScreen(): JSX.Element {
  const { navigate } = useNavigation();
  const { t } = useTranslation();
  const { searchUser, addPartnership } = usePartnership();
  const { translateError } = useFirebase();

  const [isSearching, setIsSearching] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [error, setError] = useState<string>();
  const [partner, setPartner] = useState<User>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  async function handleSubmit({ mail }: FormData) {
    setIsSearching(true);
    setError(undefined);
    setPartner(undefined);

    await searchUser(mail)
      .then((p) => (p ? setPartner(p) : setError('User not found')))
      .catch((e) => setError(translateError(e.message)))
      .finally(() => setIsSearching(false));
  }

  async function requestPartnership(partner: User) {
    setIsRequesting(true);

    await addPartnership(partner)
      .then(() => navigate(-1))
      .catch((e) => setError(translateError(e.message)))
      .finally(() => setIsRequesting(false));
  }

  useLayout(() => ({ activeMenu: 'partners', title: t('Add Partner'), titleSize: 'text-2xl', backButton: true }), [t]);

  return (
    <>
      <Form onSubmit={handleSubmit} className="mb-5 mt-2">
        <div className="join flex">
          <div className="flex-grow-1">
            <label className="input input-lg validator join-item w-full">
              <FiMail className="text-base-content/70" />
              <input type="email" name="mail" placeholder={t('Partner email')} required />
            </label>
            <div className="validator-hint hidden">{t('Please enter a valid email')}</div>
          </div>

          <button className="btn btn-primary btn-lg join-item" disabled={isSearching}>
            <FaSearch />
          </button>
        </div>
      </Form>

      {isSearching && (
        <div className="flex flex-col items-center">
          <span className="loading loading-ring loading-xl" />
        </div>
      )}

      {partner && (
        <ul className="list bg-base-100 rounded-box shadow-md mb-5">
          <li className="list-row flex">
            <div className="flex-grow-1">
              <div className="font-bold line-clamp-1 text-lg">{partner.name}</div>
              <div className="text-base-content/70 line-clamp-1">{partner.mail}</div>
            </div>
            <div className="flex flex-col justify-center whitespace-nowrap">
              <Button
                className="btn btn-primary btn-md"
                loading={isRequesting}
                onClick={() => requestPartnership(partner)}
              >
                {t('Request')}
              </Button>
            </div>
          </li>
        </ul>
      )}

      {error && <Error msg={error} />}
    </>
  );
}
